import { memo, useCallback } from 'react';

import { BlockIcon, LegalPersonIcon, RestritoIcon } from 'components/Icons';
import AnexoIcon from 'components/Icons/AnexoIcon';
import PautaEleicaoModal, {
  IPautaEleicaoGenericProps,
} from 'components/Modal/__LEGACY__/PautaEleicaoModal/PautaEleicaoModal';
import { AssembleiaTipoPauta } from 'helpers/enums/AssembleiaPautaStatus';
import { clsx } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import {
  AgencyIcon,
  Button,
  ButtonThemes,
  CheckCircleIcon,
  ChevronArrowDownIcon,
  Col,
  Collapse,
  ComunidadeIcon,
  LockIcon,
  Row,
  TimesCircleIcon,
} from 'plataforma-fundacao-componentes';
import { useDispatch } from 'react-redux';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import {
  getStatusClassName,
  STATUS_PAUTA_BLOQUEADA,
  STATUS_PAUTA_ENCERRADA,
  STATUS_PAUTA_LIBERADA,
} from './Pautas';

import { getStatusName } from 'components/__SHARED__/AssembleiaPautasNew/Pautas';
import CountdownTimer from 'components/__SHARED__/CountdownTimer/CountdownTimer';
import styles from './styles.module.scss';

interface AssembleiaPautaNewAssessorProps {
  opened: boolean;
  allwaysOpened?: boolean;
  toggleOpened: (opened: boolean) => void;
  titulo: string;
  descricao?: string;
  callBackZerouTempo?: () => void;
  tempo?: number;
  itens?: {
    idItemPauta: number;
    descricao: string;
    flgAbstencao: number;
    representantesChapa: {}[];
  }[];
  status: any;
  hasPermissionToSeeAnexos?: boolean;
  onClickAnexos?: () => void;
  actions?: React.ReactNode;
  flgSecreta?: boolean;
  flgRestrita?: boolean;
  tipoPauta?: AssembleiaTipoPauta;
  nucleou?: string;
  agency?: string;
  electionCommission?: () => void;
  presential?: boolean;
  dataTestid?: any;
}

function AssembleiaPautaNewAssessor(props: AssembleiaPautaNewAssessorProps) {
  const dispatchAction = useDispatch();

  const handleToggle = () => {
    if (!props.allwaysOpened && typeof props.toggleOpened === 'function') {
      props.toggleOpened(!props.opened);
    }
  };

  const getStatusIcon = () => {
    switch (props.status) {
      case STATUS_PAUTA_LIBERADA:
        return <CheckCircleIcon />;
      case STATUS_PAUTA_ENCERRADA:
        return <TimesCircleIcon />;
      case STATUS_PAUTA_BLOQUEADA:
        return <LockIcon />;
      default:
        return '-';
    }
  };

  const openModalDetalhesChapa = useCallback(
    (item: any) => {
      const modalKey: string = getUniqueKey();
      dispatchAction(
        modalsActionsOpen(
          PautaEleicaoModal,
          {
            genericProps: {
              key: modalKey,
              item: item,
            } as IPautaEleicaoGenericProps,
          },
          modalKey
        )
      );
    },
    [dispatchAction]
  );

  const sortOptions = useCallback(
    (
      opA: {
        idItemPauta: number;
        descricao: string;
        flgAbstencao: number;
        representantesChapa: {}[];
      },
      opB: {
        idItemPauta: number;
        descricao: string;
        flgAbstencao: number;
        representantesChapa: {}[];
      }
    ) => {
      if (!opA.flgAbstencao && opB.flgAbstencao) return -1;
      if (opA.flgAbstencao && !opB.flgAbstencao) return 1;
      return 0;
    },
    []
  );

  return (
    <div className={clsx([styles.root, getStatusClassName(props.status)])}>
      {props.tipoPauta === 1 || props.tipoPauta === 2 ? (
        <div
          className={clsx([styles.external, getStatusClassName(props.status)])}
          onClick={() => {
            if (props.tipoPauta === 1) {
              props.electionCommission && props.electionCommission();
            }
          }}
        >
          {props.tipoPauta === 1 ? (
            <div className={styles.tab}>
              <div className={styles.electionCommission}>
                <LegalPersonIcon />
                Comissão eleitoral
              </div>
            </div>
          ) : (
            <div className={styles.tab}>
              <div className={styles.agencyAndNucleou}>
                <div>
                  <AgencyIcon />
                  <span>
                    Agência <strong>{props.agency}</strong>
                  </span>
                </div>
                <div>
                  <ComunidadeIcon />
                  <span>
                    Núcleo <strong>{props.nucleou}</strong>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : undefined}
      <div
        className={clsx([
          styles.inside,
          props.tipoPauta === 1 || props.tipoPauta === 2
            ? styles.removeLeftBorder
            : '',
          getStatusClassName(props.status),
          props.opened ? styles.opened : '',
          props.allwaysOpened ? styles.allwaysOpened : '',
        ])}
      >
        <div
          data-testid={props.dataTestid}
          className={styles.header}
          onClick={handleToggle}
        >
          <div className={styles.firstLine}>
            <div className={styles.firstCol}>
              {!props.presential ? (
                <div className={clsx([styles.timer, 'nunito'])}>
                  <CountdownTimer
                    counter={props.tempo!}
                    onCompleted={
                      !props.presential ? props.callBackZerouTempo : undefined
                    }
                  />
                </div>
              ) : undefined}
              <div className={styles.etiqueta}>
                <span className={styles.icon}>{getStatusIcon()}</span>
                <span className={clsx([styles.label, 'nunito'])}>
                  {getStatusName(props.status)}
                </span>
              </div>
            </div>
            {!props.allwaysOpened ? (
              <div
                className={clsx([
                  styles.arrow,
                  props.opened ? styles.cima : '',
                ])}
              >
                <ChevronArrowDownIcon />
              </div>
            ) : undefined}
          </div>
          <Row>
            <Col
              className={styles.titleContainer}
              style={{ paddingLeft: '0px' }}
            >
              {props.flgSecreta && <BlockIcon />}
              {props.flgRestrita && <RestritoIcon />}
              <div className={styles.titulo}>{props.titulo}</div>
            </Col>

            {props.hasPermissionToSeeAnexos ? (
              <Col style={{ paddingLeft: '0px' }}>
                <Button
                  theme={ButtonThemes.Secondary}
                  leftIcon={<AnexoIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onClickAnexos && props.onClickAnexos();
                  }}
                >
                  Anexos
                </Button>
              </Col>
            ) : undefined}
          </Row>
        </div>
        <Collapse opened={props.opened || props.allwaysOpened}>
          <div className={styles.corpoPauta}>
            <div className={styles.descricao}>{props.descricao}</div>
            <div className={styles.opcoesDeVoto}>
              <div className={styles.opcaoDeVotoTitle}>
                <span className={clsx([styles.nome, 'nunito'])}>
                  Opções de voto:{' '}
                </span>
              </div>
              {props!?.itens!?.sort(sortOptions).map((it) => {
                return (
                  <div key={crypto.randomUUID()}>
                    <div className={styles.opcaoDeVoto}>
                      <span className={clsx([styles.nome, 'nunito'])}>
                        {!it ? '' : it.descricao}
                      </span>
                      {props.tipoPauta === AssembleiaTipoPauta.Eleicao &&
                        it.flgAbstencao === 0 && (
                          <Button
                            theme={ButtonThemes.DefaultPrimary}
                            onClick={() => openModalDetalhesChapa(it)}
                          >
                            Ver detalhes
                          </Button>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.actionsLine}>{props.actions}</div>
          </div>
        </Collapse>
      </div>
    </div>
  );
}

export default memo(AssembleiaPautaNewAssessor);
